/* ============================================================================
   WAX SUPPLY — theme interactions
   Vanilla JS, no dependencies. Loaded defer on every template.
   Owns: scroll reveals, the full-screen mobile menu, and the cart free-ship
   progress meter (reads Dawn's cart state). Cart open/close itself stays on
   Dawn's <cart-drawer> web component.
   ============================================================================ */
(function () {
  'use strict';

  /* ---- Scroll reveals -------------------------------------------------- */
  function initReveals() {
    var els = document.querySelectorAll('[data-ws-reveal]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('ws-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('ws-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Full-screen mobile menu ---------------------------------------- */
  function initMenu() {
    var menu = document.getElementById('ws-menu');
    if (!menu) return;
    var openers = document.querySelectorAll('[data-ws-menu-open]');
    var closers = menu.querySelectorAll('[data-ws-menu-close]');

    function open() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ws-no-scroll');
      var first = menu.querySelector('a, button');
      if (first) first.focus();
    }
    function close() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ws-no-scroll');
    }
    openers.forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); open(); }); });
    closers.forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); close(); }); });
    document.addEventListener('keyup', function (e) { if (e.key === 'Escape' && menu.classList.contains('is-open')) close(); });
  }

  /* ---- Cart free-ship progress meter ----------------------------------- */
  /* Threshold in cents comes from [data-ws-freeship-cents] on the meter.
     Reads /cart.js so it stays correct even before the drawer opens, and
     refreshes on Dawn's cart-update publish + a MutationObserver fallback. */
  function money(cents) {
    try {
      return (window.Shopify && Shopify.formatMoney)
        ? Shopify.formatMoney(cents, window.wsMoneyFormat || '${{amount}}')
        : '$' + (cents / 100).toFixed(2);
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }

  function paintMeter(meter, total, threshold) {
    var pct = threshold > 0 ? Math.min(100, (total / threshold) * 100) : 100;
    var fill = meter.querySelector('[data-ws-freeship-fill]');
    var label = meter.querySelector('[data-ws-freeship-label]');
    if (fill) fill.style.width = pct.toFixed(1) + '%';
    if (label) {
      if (total >= threshold) {
        label.textContent = (label.getAttribute('data-unlocked') || 'FREE SHIPPING UNLOCKED') + ' ✓';
        meter.classList.add('is-unlocked');
      } else {
        var remaining = threshold - total;
        var tmpl = label.getAttribute('data-away') || '{amount} AWAY FROM FREE SHIPPING';
        label.textContent = tmpl.replace('{amount}', money(remaining));
        meter.classList.remove('is-unlocked');
      }
    }
  }

  function refreshMeters(cartTotal) {
    var meters = document.querySelectorAll('[data-ws-freeship]');
    if (!meters.length) return;
    var apply = function (total) {
      meters.forEach(function (m) {
        var threshold = parseInt(m.getAttribute('data-ws-freeship-cents'), 10) || 10000;
        paintMeter(m, total, threshold);
      });
    };
    if (typeof cartTotal === 'number') { apply(cartTotal); return; }
    fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { apply(cart.total_price); })
      .catch(function () {});
  }

  function initCartMeter() {
    if (!document.querySelector('[data-ws-freeship]')) return;
    refreshMeters();
    // Dawn publishes cart updates through its pub/sub; also watch the drawer DOM.
    document.addEventListener('cart:refresh', function () { refreshMeters(); });
    document.addEventListener('cart-drawer:open', function () { refreshMeters(); });
    var drawer = document.querySelector('cart-drawer');
    if (drawer && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () { refreshMeters(); });
      mo.observe(drawer, { childList: true, subtree: true });
    }
  }

  /* ---- Brand the native Shopify Forms embed (open shadow DOM) ---------- */
  /* Shopify Forms renders its fields inside an OPEN shadow root with hashed,
     app-generated classes, so ordinary theme CSS can't reach them. We inject a
     <style> into that shadow root using element/type selectors (stable across
     app updates). Idempotent + polled, since the embed hydrates async. */
  function brandForms() {
    var css =
      "*{border-radius:0 !important;}" +
      ".field,[class*=field],[class*=Field],fieldset{background:transparent !important;box-shadow:none !important;}" +
      "input:not([type=checkbox]):not([type=radio]),textarea,select{border:1px solid #0A0A0A !important;border-radius:0 !important;padding:26px 14px 10px !important;font-family:'Archivo',sans-serif !important;font-size:15px !important;background:#fff !important;color:#0A0A0A !important;box-shadow:none !important;}" +
      "input:focus,textarea:focus,select:focus{outline:none !important;border-color:#0A0A0A !important;box-shadow:0 0 0 1px #0A0A0A !important;}" +
      "input[type=checkbox],input[type=radio]{accent-color:#0A0A0A !important;}" +
      "label{font-family:'IBM Plex Mono',monospace !important;font-size:11px !important;letter-spacing:0.06em !important;text-transform:uppercase !important;color:#3B3936 !important;}" +
      "button[type=submit]{background:#0A0A0A !important;color:#fff !important;border:0 !important;border-radius:0 !important;letter-spacing:0.08em !important;text-transform:uppercase !important;font-family:'Archivo',sans-serif !important;padding:16px 34px !important;}" +
      "textarea[class]{padding:38px 14px 12px !important;}";
    document.querySelectorAll('shopify-forms-embed').forEach(function (el) {
      var sr = el.shadowRoot;
      if (!sr || sr.getElementById('ws-form-brand')) return;
      var s = document.createElement('style');
      s.id = 'ws-form-brand';
      s.textContent = css;
      sr.appendChild(s);
    });
  }
  function initFormBrand() {
    brandForms();
    var tries = 0;
    var iv = setInterval(function () { brandForms(); if (++tries > 40) clearInterval(iv); }, 250);
  }

  /* ---- Slide-over cart drawer ------------------------------------------ */
  /* Reuses Shopify's cart AJAX (/cart.js, /cart/change.js, /cart/add.js). The
     drawer body is rendered from /cart.js; window.wsCart (from ws-cart-drawer
     snippet) supplies compare-at / formula no. / pack descriptor + the pairs-
     with upsell. No-JS shoppers fall back to the /cart page. */
  function initDrawer() {
    var d = document.getElementById('ws-drawer');
    if (!d || !window.wsCart) return;
    var cfg = window.wsCart;
    var body = d.querySelector('[data-ws-drawer-body]');
    var foot = d.querySelector('[data-ws-d-foot]');

    function open() { d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false'); document.body.classList.add('ws-no-scroll'); refresh(); }
    function close() { d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true'); document.body.classList.remove('ws-no-scroll'); }
    function sized(url, w) { return url ? url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w : ''; }
    function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

    function paintShip(total) {
      var fill = d.querySelector('[data-ws-d-ship-fill]'), lab = d.querySelector('[data-ws-d-ship-label]');
      var pct = cfg.threshold > 0 ? Math.min(100, total / cfg.threshold * 100) : 100;
      if (fill) fill.style.width = pct.toFixed(1) + '%';
      if (lab) lab.textContent = total >= cfg.threshold ? 'Free shipping unlocked ✓' : (money(cfg.threshold - total) + ' away from free shipping');
    }
    function paintPairs(cart) {
      var p = d.querySelector('[data-ws-d-pairs]');
      if (!p) return;
      if (!cfg.pairs || cart.items.some(function (it) { return it.variant_id === cfg.pairs.id; })) { p.hidden = true; return; }
      p.hidden = false;
      d.querySelector('[data-ws-d-pairs-v]').textContent = cfg.pairs.title + ' · ' + money(cfg.pairs.price);
    }
    function itemHTML(it) {
      var meta = cfg.variants[it.variant_id] || {};
      var nm = meta.nm || (it.product_title || '').split(' Hard Wax')[0];
      var cmp = meta.cmp || 0, each = it.final_price;
      var lbs = parseFloat((it.variant_title || '').split('lb')[0]);
      var perlb = (lbs > 0) ? (money(Math.round(each / lbs)) + '/lb') : '';
      var base = meta.pack ? meta.pack : (it.variant_title || '');
      var desc = (meta.no ? meta.no + ' · ' : '') + base + (perlb ? ' · ' + perlb : '');
      return '<div class="ws-di" data-key="' + it.key + '">'
        + '<div class="ws-di__img">' + (it.image ? '<img src="' + sized(it.image, 160) + '" alt="">' : '') + '</div>'
        + '<div class="ws-di__main">'
        + '<div class="ws-di__row1"><span class="ws-di__nm">' + esc(nm) + '</span><button class="ws-di__rm" type="button" data-rm>Remove</button></div>'
        + '<div class="ws-di__desc">' + esc(desc) + '</div>'
        + '<div class="ws-di__price"><b>' + money(each) + '</b>' + (cmp > each ? ' <s>' + money(cmp) + '</s>' : '') + ' each</div>'
        + '<div class="ws-di__row2"><span class="ws-qty"><button type="button" data-dec aria-label="Decrease">−</button>'
        + '<input type="number" min="0" value="' + it.quantity + '" data-q aria-label="Quantity"><button type="button" data-inc aria-label="Increase">+</button></span>'
        + '<span class="ws-di__line">' + money(it.final_line_price) + '</span></div>'
        + '</div></div>';
    }
    function emptyHTML() {
      return '<div class="ws-di-empty"><div class="ws-di-empty__mark">+</div>'
        + '<div class="ws-di-empty__k">Your cart is empty</div>'
        + '<div class="ws-di-empty__h">Nothing here yet.</div>'
        + '<p>Eight professional formulas — wholesale direct, from $16.25/lb down to $11.93 at volume.</p>'
        + '<a class="ws-drawer__cta" href="' + cfg.shopUrl + '">Shop all formulas</a>'
        + '<a class="ws-drawer__cta ws-drawer__cta--out" href="' + cfg.samplesUrl + '">Request samples →</a></div>';
    }
    function render(cart) {
      var cEl = d.querySelector('[data-ws-d-count]');
      if (cEl) cEl.textContent = '(' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ')';
      document.querySelectorAll('[data-ws-cart-count]').forEach(function (e) { e.textContent = cart.item_count; });
      if (cart.item_count === 0) { body.innerHTML = emptyHTML(); foot.hidden = true; paintShip(0); return; }
      foot.hidden = false;
      var html = '', saved = 0;
      cart.items.forEach(function (it) {
        var meta = cfg.variants[it.variant_id] || {};
        if ((meta.cmp || 0) > it.final_price) saved += (meta.cmp - it.final_price) * it.quantity;
        html += itemHTML(it);
      });
      body.innerHTML = html;
      d.querySelector('[data-ws-d-sub]').textContent = money(cart.total_price);
      d.querySelector('[data-ws-d-checkout-sub]').textContent = money(cart.total_price);
      var saveEl = d.querySelector('[data-ws-d-save]');
      if (saved > 0) { saveEl.hidden = false; saveEl.textContent = 'You save ' + money(saved) + ' vs list'; } else { saveEl.hidden = true; }
      paintPairs(cart); paintShip(cart.total_price); bindLines();
    }
    function change(key, qty) {
      fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) })
        .then(function (r) { return r.json(); }).then(render).catch(function () {});
    }
    function bindLines() {
      body.querySelectorAll('[data-key]').forEach(function (line) {
        var key = line.getAttribute('data-key'), input = line.querySelector('[data-q]');
        line.querySelector('[data-dec]').addEventListener('click', function () { change(key, Math.max(0, (parseInt(input.value, 10) || 0) - 1)); });
        line.querySelector('[data-inc]').addEventListener('click', function () { change(key, (parseInt(input.value, 10) || 0) + 1); });
        input.addEventListener('change', function () { change(key, Math.max(0, parseInt(input.value, 10) || 0)); });
        line.querySelector('[data-rm]').addEventListener('click', function () { change(key, 0); });
      });
    }
    function refresh() { fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }).then(render).catch(function () {}); }

    var pairsAdd = d.querySelector('[data-ws-d-pairs-add]');
    if (pairsAdd) pairsAdd.addEventListener('click', function () {
      if (!cfg.pairs) return;
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: cfg.pairs.id, quantity: 1 }] }) })
        .then(function () { refresh(); }).catch(function () {});
    });
    var icon = document.getElementById('cart-icon-bubble');
    if (icon) icon.addEventListener('click', function (e) { e.preventDefault(); open(); });
    d.querySelectorAll('[data-ws-drawer-close]').forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keyup', function (e) { if (e.key === 'Escape' && d.classList.contains('is-open')) close(); });
    window.wsOpenCart = open; window.wsRefreshCart = refresh;
    refresh();
  }

  function init() { initReveals(); initMenu(); initCartMeter(); initFormBrand(); initDrawer(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
